import { useEffect, useRef } from 'react';

interface HorizontalPointerDragOptions {
  /** When true, ignore drags that start on interactive controls (buttons/links/inputs). */
  ignoreInteractiveTargets?: boolean;
}

export default function useHorizontalPointerDrag<T extends HTMLElement>(
  options: HorizontalPointerDragOptions = {},
) {
  const { ignoreInteractiveTargets = true } = options;
  const scrollRef = useRef<T | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.style.touchAction = 'pan-x';

    let isDragging = false;
    let mode: 'pending' | 'horizontal' | 'vertical' = 'pending';
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let capturedPointerId: number | null = null;
    const ADMIT_THRESHOLD = 8;

    const capturePointer = (pointerId: number) => {
      if (capturedPointerId === pointerId) return;
      try {
        el.setPointerCapture(pointerId);
        capturedPointerId = pointerId;
      } catch {}
    };

    const releasePointer = (pointerId: number) => {
      if (capturedPointerId !== pointerId) return;
      try {
        el.releasePointerCapture(pointerId);
      } catch {}
      capturedPointerId = null;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button && e.button !== 0) return;

      if (ignoreInteractiveTargets) {
        const target = e.target as HTMLElement | null;
        const interactiveAncestor = target?.closest('button,a,input,textarea,select,[role="button"]');
        if (interactiveAncestor) return;
      }

      startX = e.clientX;
      startY = e.clientY;
      startScrollLeft = el.scrollLeft;
      isDragging = true;
      mode = 'pending';

      if (e.pointerType === 'mouse') {
        capturePointer(e.pointerId);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (mode === 'pending') {
        if (Math.abs(dx) > ADMIT_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.1) {
          mode = 'horizontal';
          el.style.cursor = 'grabbing';
        } else if (Math.abs(dy) > ADMIT_THRESHOLD && Math.abs(dy) > Math.abs(dx)) {
          mode = 'vertical';
          isDragging = false;
          el.style.cursor = '';
          return;
        } else {
          return;
        }
      }

      if (mode === 'horizontal') {
        el.scrollLeft = startScrollLeft - dx;
        e.preventDefault();
        e.stopPropagation();
        capturePointer(e.pointerId);
      }
    };

    const endDrag = (e: PointerEvent) => {
      isDragging = false;
      mode = 'pending';
      el.style.cursor = '';
      if (e?.pointerId !== undefined) {
        releasePointer(e.pointerId);
      }
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
      el.style.cursor = '';
      if (capturedPointerId !== null) {
        releasePointer(capturedPointerId);
      }
    };
  }, [ignoreInteractiveTargets]);

  return scrollRef;
}
